import {
  axiosGetPaginatedRequest,
  axiosGetRequest,
  axiosMultipartPostRequest,
  axiosPatchRequest,
  axiosPostRequest,
  axiosPutRequest,
  getCommunityRoute,
  getCommunityRouteV3,
  getIdentityRoute,
  getStoriesRoute,
} from '../../api';
import { Member } from '../../profile';
import { MemberInfo } from '../../profile/model/MemberInfo';
import { Story } from '../../stories';
import { CreatePrivateRoomDto, CreateShimMemberDto, Room } from './models';
import { RoomAISettings, RoomAIConfiguration, RoomAIConfigurationUpdate, WebSearchBoost, WebSearchType, roomAIChatEngines } from './models/RoomAISettings';
import { CreateSubgroupDto } from './models/CreateSubgroupDto';
import { RoomDisplayName, RoomDisplayNameConfirmation, RoomDisplayNameStatus } from './models/RoomDisplayName';
import { LinkedRoom, LinkedRoomRelationshipType } from './models/LinkedRoom';

const SEARCH_RESULTS_MAX = 20;
const PAGE_SIZE = 20;
const SMALL_PAGE_SIZE = 5;

const getRoomsRoute = () => `${getIdentityRoute()}/member/suggestedrooms`;
const getRoomDetailsRoute = () => `${getCommunityRoute()}/room`;
const getRoomSubgroupsRoute = () => `${getCommunityRouteV3()}/rooms`;
const getMemberRoomsRoute = () => `${getIdentityRoute()}/member/joinedrooms`;

/**
 * Fetch the rooms that the user is a member of.
 */
export const fetchMemberRoomsCall = () => {
  return axiosGetRequest<Room[]>(`${getMemberRoomsRoute()}/${-1}`);
};

export const fetchRoomSubgroupsCall = (
  roomId: string,
  isMember: boolean,
  isModerator?: boolean
) => {
  return axiosGetRequest<Room[]>(
    `${getRoomSubgroupsRoute()}/${-1}?roomId=${roomId}&isMember=${isMember}&isModerator=${isModerator}`
  );
};

export const fetchRoomsByTopicCall = (topic: string) => {
  return axiosGetRequest<Room[]>(
    `${getCommunityRoute()}/rooms/-1?topic=${topic}`
  );
};

export const fetchRoomCall = (roomId: string) => {
  return axiosGetRequest<Room>(`${getRoomDetailsRoute()}/${roomId}`);
};

export const findRoomsCall = (searchString: string) => {
  const encodedSearchString = encodeURIComponent(searchString);
  return axiosGetRequest<Room[]>(
    `${getRoomsRoute()}/${SEARCH_RESULTS_MAX}?title=${encodedSearchString}&includeismember=true`
  );
};

export const joinRoomCall = (roomId: string) => {
  return axiosPatchRequest<void, Room>(
    `${getIdentityRoute()}/room/${roomId}/join`
  );
};

export const leaveRoomCall = (roomId: string) => {
  return axiosPatchRequest<void, Room>(
    `${getIdentityRoute()}/room/${roomId}/leave`
  );
};

export const moderatorRemoveMemberFromRoomCall = (
  memberId: string,
  roomId: string
) => {
  return axiosPatchRequest<void, string>(
    `${getIdentityRoute()}/moderator/member/${memberId}/room/${roomId}/leave`
  );
};

export const fetchSuggestedRoomsCall = (
  xMsContinuationToken?: string,
  numrecs?: number
) => {
  return axiosGetPaginatedRequest<Room[]>(
    `${getIdentityRoute()}/member/suggestedrooms/${
      numrecs || SMALL_PAGE_SIZE
    }?sortby=1&includeismember=false`,
    xMsContinuationToken
  );
};

export const visitRoomCall = (roomId: string) => {
  return axiosPatchRequest<void, Room>(
    `${getIdentityRoute()}/room/${roomId}/visit`
  );
};

export const fetchPeopleInTheRoomCall = (
  roomId: string,
  xMsContinuationToken?: string,
  numrecs?: number
) => {
  return axiosGetPaginatedRequest<MemberInfo[]>(
    `${getRoomDetailsRoute()}/${roomId}/members/${numrecs || PAGE_SIZE}`,
    xMsContinuationToken
  );
};

export const findLiknedRoomsCall = (
  roomId: string,
  relationshipType: LinkedRoomRelationshipType = LinkedRoomRelationshipType.ALL,
  xMsContinuationToken?: string,
  numrecs?: number
) => {
  return axiosGetPaginatedRequest<LinkedRoom[]>(
    `${getCommunityRouteV3()}/linkedrooms/${roomId}?relationshiptype=${relationshipType}&numrecs=${
      numrecs || PAGE_SIZE
    }`,
    xMsContinuationToken
  );
};

export const fetchPeopleInTheRoomCountCall = (roomId: string) => {
  return axiosGetRequest<number>(
    `${getRoomDetailsRoute()}/${roomId}/membercount`
  );
};

export const addMemberToPrivateRoomCall = (roominviteid: string) => {
  return axiosPostRequest<undefined, Room>(
    `${getRoomDetailsRoute()}/${roominviteid}`
  );
};

export const fetchModeratorsInTheRoomCall = (
  roomId: string,
  xMsContinuationToken?: string,
  numrecs?: number
) => {
  return axiosGetPaginatedRequest<MemberInfo[]>(
    `${getRoomDetailsRoute()}/${roomId}/moderators/${numrecs || PAGE_SIZE}`,
    xMsContinuationToken
  );
};

export const fetchModeratorsInTheRoomCountCall = (roomId: string) => {
  return axiosGetRequest<number>(
    `${getRoomDetailsRoute()}/${roomId}/moderatorcount`
  );
};

export const fetchContentForCarouselCall = (roomId: string) => {
  return axiosGetRequest<Story[]>(
    `${getStoriesRoute()}/featuredstories/room/${roomId}`
  );
};

export const fetchFeaturedContentCall = (roomId: string) => {
  return axiosGetRequest<Story[]>(
    `${getStoriesRoute()}/featuredcontent/room/${roomId}/-1`
  );
};

export const fetchFeaturedStoryCall = (storyId: string) => {
  return axiosGetRequest<Story>(
    `${getStoriesRoute()}/featuredstory/${storyId}`
  );
};

export const fetchCurrentMemberInRoomCall = (roomId: string) => {
  return axiosGetRequest<Member>(`${getIdentityRoute()}/member/room/${roomId}`);
};

export const fetchModeratedRoomsCall = () => {
  return axiosGetRequest<Room[]>(
    `${getCommunityRoute()}/contentmoderatedrooms/member/-1`
  );
};

export const createPrivateRoomCall = (
  createPrivateRoomDto: CreatePrivateRoomDto
) => {
  return axiosPostRequest<CreatePrivateRoomDto, Room>(
    `${getRoomDetailsRoute()}`,
    createPrivateRoomDto
  );
};

export const createSubgroupCall = (createSubgroupDto: CreateSubgroupDto) => {
  return axiosPostRequest<CreatePrivateRoomDto, Room>(
    `${getRoomDetailsRoute()}?roomId=${createSubgroupDto.roomId}`,
    { title: createSubgroupDto.title, content: createSubgroupDto.content }
  );
};

export const createShimMemberCall = (
  createShimMemberDto: CreateShimMemberDto
) => {
  return axiosPostRequest<CreateShimMemberDto, Member>(
    `${getIdentityRoute()}/member/shim`,
    createShimMemberDto
  );
};

const getRoomDisplayNameRoute = (roomId: string) =>
  `${getIdentityRoute()}/member/room/${encodeURIComponent(roomId)}/displayname`;

/** Resolves null when the room's effective onboarding has no room-name step (HTTP 204). */
export const fetchRoomDisplayNameStatusCall = async (roomId: string) =>
  (await axiosGetRequest<RoomDisplayNameStatus | ''>(getRoomDisplayNameRoute(roomId))) || null;

/** Assigns (or returns the already-assigned) generated name for the exact room. */
export const assignRoomDisplayNameCall = (roomId: string) =>
  axiosPostRequest<void, RoomDisplayName>(`${getRoomDisplayNameRoute(roomId)}/assign`);

/** Acknowledges the assigned name or saves the chosen name; 409 means the name is taken. */
export const confirmRoomDisplayNameCall = (roomId: string, confirmation: RoomDisplayNameConfirmation) =>
  axiosPutRequest<RoomDisplayNameConfirmation, RoomDisplayName>(getRoomDisplayNameRoute(roomId), confirmation);

export const fetchPrivateRoomCodeCall = (roomId: string) => {
  return axiosPatchRequest<void, string>(
    `${getCommunityRoute()}/roominvite/${roomId}/code`
  );
};

export const submitConsentToJoinRoomCall = (roomId: string) => {
  return axiosPatchRequest<void, string>(
    `${getIdentityRoute()}/member/room/${roomId}/consent`
  );
};

export const fetchRoomAISettingsCall = (roomId: string) => {
  return axiosGetRequest<RoomAISettings>(
    `${getRoomDetailsRoute()}/${roomId}/aisettings`
  );
};

const normalizeAIConfiguration = (configuration: RoomAIConfiguration): RoomAIConfiguration => {
  const modes: Record<string, WebSearchType> = { grounded: 'grounded', Grounded: 'grounded', groundedCustom: 'groundedCustom', GroundedCustom: 'groundedCustom' };
  const boosts: Record<string, WebSearchBoost> = { normal: 'normal', Normal: 'normal', boost: 'boost', Boost: 'boost', superBoost: 'superBoost', SuperBoost: 'superBoost' };
  const settings = configuration.settings;
  if (typeof configuration.defaultRole !== 'string' || !configuration.defaultRole.trim())
    throw new Error('Invalid room AI default role');
  const webSearchType = modes[settings.webSearchType];
  if (!webSearchType || !Array.isArray(settings.webSearchSites)) throw new Error('Invalid room search configuration');
  const webSearchSites = settings.webSearchSites.map((site) => {
    const boost = boosts[site.boost];
    if (!boost || typeof site.url !== 'string') throw new Error('Invalid room search site');
    return { ...site, boost };
  });
  const chatEngine = settings.chatEngine ?? 'v1';
  if (!roomAIChatEngines.includes(chatEngine)) throw new Error('Invalid room chat engine');
  return { ...configuration, canChangeChatEngine: configuration.canChangeChatEngine === true,
    settings: { ...settings, model: settings.model ?? null,
      roleOverride: settings.roleOverride ?? null, webSearchType, webSearchSites, chatEngine } };
};

export const fetchRoomAIConfigurationCall = (roomId: string) =>
  axiosGetRequest<RoomAIConfiguration>(`${getRoomDetailsRoute()}/${encodeURIComponent(roomId)}/aiconfiguration`).then(normalizeAIConfiguration);

export const fetchRoomAILogosCall = (roomId: string) =>
  axiosGetRequest<string[]>(`${getRoomDetailsRoute()}/${encodeURIComponent(roomId)}/aiconfiguration/logos`);

export const uploadRoomAILogoCall = (roomId: string, file: File,
  onProgress?: (progress: { loaded: number; total?: number }) => void) => {
  const form = new FormData();
  form.append('file', file);
  return axiosMultipartPostRequest<FormData, string>(
    `${getRoomDetailsRoute()}/${encodeURIComponent(roomId)}/aiconfiguration/logos`, form, undefined, onProgress);
};

type RoomAIBrandingListener = (roomId: string, settings?: RoomAISettings) => void;

const roomAIBrandingListeners = new Set<RoomAIBrandingListener>();

export const subscribeRoomAIBrandingChanges = (listener: RoomAIBrandingListener) => {
  roomAIBrandingListeners.add(listener);
  return () => { roomAIBrandingListeners.delete(listener); };
};

export const updateRoomAIConfigurationCall = (roomId: string, data: RoomAIConfigurationUpdate) =>
  axiosPutRequest<RoomAIConfigurationUpdate, RoomAIConfiguration>(
    `${getRoomDetailsRoute()}/${encodeURIComponent(roomId)}/aiconfiguration`, data
  ).then(normalizeAIConfiguration).then((configuration) => {
    roomAIBrandingListeners.forEach((listener) => listener(configuration.roomId, configuration.settings));
    return configuration;
  });

export const updateRoomAISettingsCall = (
  roomId: string,
  data: RoomAISettings
) => {
  return axiosPutRequest<RoomAISettings, RoomAISettings>(
    `${getRoomDetailsRoute()}/${roomId}/aisettings`,
    data
  );
};
